<template>
    <div class="project-persons-calendar">
        <template
            v-for="project in projects"
            :key="project.id"
        >
            <div class="title-row" >
                <div class="title-row-content">
                    {{ project.title }}
                </div>
            </div>
            <template
                v-for="taskId in orderedTasksForProject(project)"
                :key="project.id+taskId"
            >
                <div
                    class="task-row"
                >
                    <div class="task_title d-flex justify-content-between align-items-center">
                        <span>{{ tasks[taskId]['title'] }}</span>
                        <button
                            class="btn btn-link btn-lg"
                            @click="personsExpanded = !personsExpanded"
                        >
                            <i v-if="personsExpanded" class="icofont-collapse"></i>
                            <i v-if="!personsExpanded" class="icofont-expand-alt"></i>
                        </button>

                    </div>
                    <button
                        v-for="date in dates"
                        :key="date.formatted + taskId + 'summary'"
                        class="task_summary-day"
                        :style="{
                            ...dayStyle(date),
                            background: (tasks[taskId]?.personsCount?.[date.formatted] || tasks[taskId]?.equipmentsCount?.[date.formatted]) ? getStatusBackground(tasks[taskId]) : '',
                            color: (tasks[taskId]?.personsCount?.[date.formatted] || tasks[taskId]?.equipmentsCount?.[date.formatted]) ? getStatusColor(tasks[taskId]) : '',
                        }"
                        @click="openPlanningFormForTaskAndDate(date, taskId)"
                    >
                        <template
                            v-if="tasks[taskId]?.personsCount?.[date.formatted]"
                            v-for="(number, index) in Object.values(tasks[taskId].personsCount[date.formatted])"
                            :key="index + date.formatted + taskId + 'summary-count'"
                        >
                            {{ index > 0 ? '+' : '' }}{{ number }}
                        </template>
                        <i 
                            v-if="tasks[taskId]?.hasJobMismatch?.[date.formatted] || tasks[taskId]?.hasNonBillable?.[date.formatted]"
                            class="icofont-exclamation-tringle ml-1"
                        ></i>
                    </button>
                </div>
                <div
                    v-if="personsExpanded"
                    v-for="personId in orderedPersonsForTask(taskId)"
                    :key="personId + taskId"
                    class="person-row"
                >
                    <div
                        class="person-name d-flex align-items-center"
                        :style="{
                            'border-left-color': persons[personId]['status_color']
                        }"
                        @mouseenter="showPersonTooltip($event, personId, null)"
                        @mouseleave="hidePersonTooltip($event, personId)"
                    >
                        <span
                            class="status-dot"
                            :class="
                                persons[personId]['badge_type'] === 'in' ? 'green' : 'red'
                            "
                        ></span>
                        <a
                            :href="route('person.show', persons[personId].id)"
                            class="ml-1"
                        >
                            {{ persons[personId].name }}
                        </a>
                    </div>
                    <div
                        v-for="date in dates"
                        :key="date.formatted + personId + taskId"
                        :style="dayStyle(date, personId, taskId)"
                        class="person-tasks"
                        @mousedown.prevent="handleMouseDown($event, 'person', personId, date, taskId)"
                        @mouseenter="handleMouseEnter('person', personId, date.formatted, taskId)"
                        @mouseover="!isDragging && showPersonTooltip($event, personId, date)"
                        @mouseleave="!isDragging && hidePersonTooltip($event, personId)"
                        @click.self="openPlanningFormForPersonAndDate($event, personId, date, taskId)"
                    >
                        <div
                            v-for="block in getBlocksForDatePersonAbsences(date, personId)"
                            :key="'absence'+block.absence_id+date.formatted+personId+taskId"
                            :style="block.style"
                            class="absence-block"
                        >
                            {{ block.absence_title }}
                        </div>
                        <button
                            v-for="block in getBlocksForDatePersonTask(date, personId, taskId)"
                            :key="block.slot_id+date.formatted+personId+taskId"
                            :style="block.style"
                            @click="openPlanningFormForSlot($event, block.slot_id)"
                            class="slot-btn"
                        >
                            {{ block.task_title }} 
                            {{ block.project_title ? ' | '+block.project_title : '' }}
                        </button>
                    </div>
                </div>

                <!-- Equipment Rows -->
                <div
                    v-if="personsExpanded && tasks[taskId]?.equipments"
                    v-for="equipmentId in orderedEquipmentForTask(taskId)"
                    :key="equipmentId + taskId + 'equipment'"
                    class="equipment-row"
                >
                    <div
                        class="equipment-name d-flex align-items-center"
                    >
                        <i class="icofont-vehicle-cement mr-2"></i>
                        <a :href="route('equipment.show', equipments[equipmentId].id)">
                            {{ equipments[equipmentId].title }}
                        </a>
                    </div>
                    <div
                        v-for="date in dates"
                        :key="date.formatted + equipmentId + taskId + 'equipment-date'"
                        :style="dayStyleEquipment(date, equipmentId, taskId)"
                        class="person-tasks"
                        @mousedown.prevent="handleMouseDown($event, 'equipment', equipmentId, date, taskId)"
                        @mouseenter="handleMouseEnter('equipment', equipmentId, date.formatted, taskId)"
                        @click.self="openPlanningFormForEquipmentAndDate($event, equipmentId, date, taskId)"
                    >
                        <button
                            v-for="block in getBlocksForDateEquipmentTask(date, equipmentId, taskId)"
                            :key="block.slot_id + date.formatted + equipmentId + taskId + 'equipment-slot'"
                            :style="block.style"
                            @click="openPlanningFormForSlot($event, block.slot_id)"
                            class="slot-btn"
                        >
                            {{ block.task_title }} -
                            {{ block.project_title }}
                        </button>
                    </div>
                </div>
                <!-- End Equipment Rows -->

            </template>
        </template>
    </div>

    <PersonTooltipComponent
        ref="personTooltip"
    />

    <!-- Inlined Selection Actions Bar -->
    <teleport to="body">
        <div v-if="selectedCells.size > 0" class="selection-actions-bar-inline">
            <button @click="handlePlanSelection" class="btn btn-primary">
                {{ trans('Plan selection') }}
            </button>
            <button @click="handleClearPlanning" class="btn btn-danger">
                {{ trans('Clear planning') }}
            </button>
            <button @click="handleClearSelection" class="btn btn-outline-secondary">
                {{ trans('Clear selection') }}
            </button>
        </div>
    </teleport>

    <ConfirmationModalComponent
        v-if="showClearPlanningConfirmation"
        :body="'Are you sure you want to clear all planning for the selected cells?'"
        :subtitle="'Warning'"
        @close="showClearPlanningConfirmation = false"
        @succes="confirmClearPlanning"
    />

</template>

<script>
import {mapState} from "vuex";
import {ZOOM_SETTINGS} from "../../../constants";
import {emitter} from "../../../plugins/Mitt";
import PersonTooltipComponent from "../../person/PersonTooltipComponent.vue";
import ConfirmationModalComponent from "../../helpers/ConfirmationModalComponent.vue";

export default {
    components: {
        PersonTooltipComponent,
        ConfirmationModalComponent
    },
    data(){
        return {
            personsExpanded: true,
            projects: [],
            tasks: {},
            persons: {},
            equipments: {},
            slots: {},
            absences: {},
            absenceTypes: {},
            defaultWeekschedules: {},
            plannedWeekschedules: {},

            isDragging: false,
            dragStartCellInfo: { type: null, itemId: null, dateFormatted: null, taskId: null },
            selectedCells: new Set(),
            boundHandleDocumentMouseMove: null,
            boundHandleDocumentMouseUp: null,
            showClearPlanningConfirmation: false,
        }
    },
    computed: {
        ...mapState('planning', [
            'dates',
            'dateStart',
            'dateEnd',
            'currentZoom',
            'search'
        ]),
        ...mapState('planningFilter', ['filter','filterLoaded']),

        orderedDatesFormatted() {
            return this.dates.map(d => d.formatted);
        },

        orderedPersonsForTask() {
            return (taskId) => {
                const task = this.tasks[taskId];
                if (!task?.persons) return [];
                
                return [...task.persons].sort((a, b) => {
                    const personA = this.persons[a];
                    const personB = this.persons[b];
                    
                    // First compare by job_id
                    if (personA.job_id !== personB.job_id) {
                        return (personA.job_id || 0) - (personB.job_id || 0);
                    }
                    
                    // If job_ids are equal, compare by name
                    return personA.name.localeCompare(personB.name);
                });
            };
        },

        orderedEquipmentForTask() {
            return (taskId) => {
                const task = this.tasks[taskId];
                if (!task?.equipments) return [];
                
                return [...task.equipments].sort((a, b) => {
                    const equipmentA = this.equipments[a];
                    const equipmentB = this.equipments[b];
                    return equipmentA.title.localeCompare(equipmentB.title);
                });
            };
        },

        orderedTasksForProject() {
            return (project) => {
                if (!project?.tasks || !Array.isArray(project.tasks)) return [];
                
                return [...project.tasks].sort((a, b) => {
                    const taskA = this.tasks[a];
                    const taskB = this.tasks[b];
                    
                    if (!taskA || !taskB) return 0;
                    
                    return taskA.title.localeCompare(taskB.title);
                });
            };
        },
    },
    methods: {
        showPersonTooltip(event, personId, date) {
            this.$refs.personTooltip.activateTooltipForPerson(event, personId, date?.formatted);
        },
        hidePersonTooltip(event, personId) {
            this.$refs.personTooltip.deactivateOrCancelTooltip(event, personId);
        },

        openPlanningFormForPersonAndDate(event, personId, date, taskId){
            if (this.isDragging) return; 
            if (event.ctrlKey || event.metaKey) return; // Don't open form if Ctrl/Cmd was part of the click
            
            // Hide tooltip when opening planning form
            this.hidePersonTooltip(event, personId);
            
            emitter.emit('open-planning-form', {
                personId: personId,
                taskId: taskId,
                dateStart: date.formatted,
                dateEnd: date.formatted,
            });
        },
        openPlanningFormForSlot(event, slotId){
            if (event.ctrlKey || event.metaKey) return; // Don't open if Ctrl/Cmd was used

            emitter.emit('open-planning-form', {
                slotId: slotId,
            });
        },
        openPlanningFormForEquipmentAndDate(event, equipmentId, date, taskId){
            if (this.isDragging) return;
            if (event.ctrlKey || event.metaKey) return; // Don't open form if Ctrl/Cmd was part of the click

            emitter.emit('open-planning-form', { // Or a new event like 'open-equipment-planning-form'
                equipmentId: equipmentId,
                taskId: taskId,
                dateStart: date.formatted,
                dateEnd: date.formatted,
                type: 'equipment' // Explicitly pass type
            });
        },
        dayStyle(date, personId = null, taskIdForSelectionCheck = null) {
            let dayColor = '#FFF';
            let finalBorderStyles = {
                borderColor: '#CCC', 
                borderWidth: '1px',
                borderStyle: 'solid' 
            };

            if (personId) {
                const weekSchedule = this.getWeekschedulForDateAndPerson(date.formatted, this.persons[personId]);
                if (weekSchedule && !weekSchedule.is_working) {
                    dayColor = '#AAA';
                }
                finalBorderStyles = this.getBorderColorForDateAndPerson(date.formatted, this.persons[personId]);
            }

            if (personId && taskIdForSelectionCheck && this.isCellSelected('person', personId, date.formatted, taskIdForSelectionCheck)) {
                finalBorderStyles.borderColor = '#FFD700'; 
                finalBorderStyles.borderWidth = '2px'; 
            }

            return{
                width: ZOOM_SETTINGS[this.currentZoom].dayWidth[date.localDate.dayOfWeek().value()]+'px',
                background: dayColor,
                ...finalBorderStyles,
            }
        },
        getData() {
            if( !this.filterLoaded || !this.dateStart || !this.dateEnd){
                return;
            }

            const start = this.dateStart;
            const end = this.dateEnd;

            const url = route('planning.project-persons.data');

            const allowedFilters = ['department_ids', 'team_ids'];
            const filter = Object.fromEntries(
                Object.entries(this.filter)
                    .filter(([key]) => allowedFilters.includes(key))
            );

            const params = {
                date_start: start,
                date_end: end,
                filter: filter,
                search: this.search,
            };

            axios.get(url, {params})
                .then(response => {                    
                    this.projects = response.data.projects;
                    this.tasks = response.data.tasks;
                    this.persons = response.data.persons;
                    this.equipments = response.data.equipments;
                    this.slots = response.data.slots;
                    this.absences = response.data.absences;
                    this.absenceTypes = response.data.absenceTypes;
                    this.defaultWeekschedules = response.data.default_weekschedules;
                    this.plannedWeekschedules = response.data.planned_weekschedules;
                });
        },
        getBlocksForDatePersonTask(date, personId, taskId) {
            const person = this.persons[personId];

            if (!person?.slots) {
                return [];
            }

            const slotsByDate = person.slots[date.formatted] ?? {};

            const slotsForCurrentTask = slotsByDate[taskId] ?? [];
            const otherTaskSlots = Object.entries(slotsByDate)
                .filter(([taskKey]) => Number(taskKey) !== taskId)
                .flatMap(([, slotIds]) => slotIds);

            const resultBlocks = [];

            slotsForCurrentTask.forEach(slotId => {
                resultBlocks.push(this.createBlockObject(person, slotId));
            });

            otherTaskSlots.forEach(slotId => {
                resultBlocks.push(this.createBlockObject(person, slotId, 0.3));
            });

            return resultBlocks;
        },
        createBlockObject(person, slotId, opacity = 1) {
            const slot = this.slots[slotId];
            if (!slot) return null;
            const task = this.tasks[slot.task_id];
            if (!task) return null;
            const project = Array.isArray(this.projects) ? this.projects.find(p => p.id === task.project_id) : this.projects[task.project_id];
            return {
                slot_id: slot.id,
                task_title: task.title,
                project_title: project ? project.title : '',
                style: {
                    backgroundColor: this.getStatusBackground(task),
                    color: this.getStatusColor(task),
                    opacity: opacity,
                    height: this.calculateBlockHeight(person, slot.date, slot.duration)+'%',
                }
            };
        },
        calculateBlockHeight(person, date, duration){
            const weekSchedule = this.getWeekschedulForDateAndPerson(date, person)
            const weekScheduleDuration = weekSchedule && weekSchedule.is_working ? weekSchedule.duration : 0;
            if (weekScheduleDuration === 0) return 0;
            return (duration/weekScheduleDuration * 100);

        },
        getBlocksForDatePersonAbsences(date, personId){
            const person = this.persons[personId];

            if (!person) {
                return [];
            }

            const absencesForPerson = this.absences[personId];
            if (!absencesForPerson?.[date.formatted]) {
                return [];
            }

            const absencesByDate = absencesForPerson[date.formatted];

            const resultBlocks = [];

            Object.entries(absencesByDate).forEach(([absenceId, absence]) => {
                const absenceType = this.absenceTypes[absence.absence_type_id];
                if (!absenceType) return;
                resultBlocks.push({
                    absence_id: absenceId,
                    absence_title: absenceType.title,
                    style: {
                        height: this.calculateBlockHeight(person, date.formatted, absence.duration)+'%',
                    }
                });
            });

            return resultBlocks;
        },
        getStatusBackground(item){
            return item.status_color;
        },
        getStatusColor(item){
            return item.status_text_color;
        },
        getWeekschedulForDateAndPerson(date, person){
            if (!person?.id) return null;
            
            let weekSchedule = this.plannedWeekschedules[person.id]?.[date] ?? null;
            if (!weekSchedule) {
                weekSchedule = this.defaultWeekschedules?.[date] ?? null;
            }
            return weekSchedule;
        },
        getBorderColorForDateAndPerson: function (date, person) {
            if (!person?.slots) return {
                borderColor: '#CCC',
                borderWidth: '1px',
                borderStyle: 'solid'
            };

            const slotsForDate = person.slots[date] ?? {};
            const weekSchedule = this.getWeekschedulForDateAndPerson(date, person);
            const absencesForPerson = this.absences[person.id] ?? {};
            const absencesForDate = absencesForPerson[date] ?? [];

            let timeToWorkInitially = weekSchedule?.duration ?? 0;
            let timePlanned = 0;

            absencesForDate.forEach(absence => {
                timeToWorkInitially -= absence.duration; 
            });

            if (slotsForDate) {
                Object.entries(slotsForDate).forEach(([taskId, slots]) => {
                    slots.forEach(slotId => {
                        const slot = this.slots[slotId];
                        if (slot) {
                            timePlanned += slot.duration;
                        }
                    });
                });
            }

            let remainingTimeToWork = timeToWorkInitially - timePlanned;
            
            let borderStyles = {
                borderColor: '#CCC',    
                borderWidth: '1px',
                borderStyle: 'solid'
            };

            if (!weekSchedule || !weekSchedule.is_working) {
                borderStyles.borderColor = '#E0E0E0'; 
            } else {
                if (timePlanned === 0) {
                    borderStyles.borderColor = '#CCC'; 
                } else if (remainingTimeToWork < 0) {
                    borderStyles.borderColor = '#F44336'; 
                    borderStyles.borderWidth = '2px';
                } else if (Math.abs(remainingTimeToWork) < 0.01) { 
                    borderStyles.borderColor = '#CCC'; 
                    // borderStyles.borderColor = '#4CAF50'; // Or Green for perfectly planned
                } else if (remainingTimeToWork > 0) {
                    borderStyles.borderColor = '#FF9800'; 
                    borderStyles.borderWidth = '2px';
                }
            }
            return borderStyles;
        },

        getCellIdentifier(type, itemId, dateFormatted, taskId) {
            return `${type}_${taskId}_${itemId}_${dateFormatted}`;
        },

        isCellSelected(type, itemId, dateFormatted, taskId) {
            return this.selectedCells.has(this.getCellIdentifier(type, itemId, dateFormatted, taskId));
        },

        handleCtrlClickSelect(type, itemId, dateFormatted, taskId) {
            const cellId = this.getCellIdentifier(type, itemId, dateFormatted, taskId);
            const currentTaskIdStr = taskId.toString();

            if (this.selectedCells.size > 0) {
                const firstSelectedCellId = this.selectedCells.values().next().value;
                const [firstSelectedType, firstSelectedTaskId] = firstSelectedCellId.split('_');

                // Only check task ID, allow different types
                if (currentTaskIdStr !== firstSelectedTaskId) {
                    this.selectedCells.clear();
                    this.selectedCells.add(cellId);
                } else {
                    if (this.selectedCells.has(cellId)) {
                        this.selectedCells.delete(cellId);
                    } else {
                        this.selectedCells.add(cellId);
                    }
                }
            } else {
                this.selectedCells.add(cellId);
            }
        },

        handleMouseDown(event, type, itemId, date, taskId) {
            if (event.ctrlKey || event.metaKey) {
                this.handleCtrlClickSelect(type, itemId, date.formatted, taskId);
                return;
            }

            this.isDragging = true;
            this.dragStartCellInfo = { type, itemId, dateFormatted: date.formatted, taskId };
            
            // Hide any active tooltips when dragging starts
            if (type === 'person') {
                this.hidePersonTooltip(event, itemId);
            }
            
            this.selectedCells.clear();

            this.boundHandleDocumentMouseMove = this._handleDocumentMouseMove.bind(this);
            this.boundHandleDocumentMouseUp = this._handleDocumentMouseUp.bind(this);

            document.addEventListener('mousemove', this.boundHandleDocumentMouseMove);
            document.addEventListener('mouseup', this.boundHandleDocumentMouseUp);
        },

        _handleDocumentMouseMove(event){
            // This method seems to be empty, if it was intended for real-time feedback during drag,
            // it could be implemented here. For selection logic, handleMouseEnter is key.
        },

        handleMouseEnter(type, itemId, dateFormatted, taskId) {
            if (!this.isDragging) return;

            // Only check task ID, allow different types
            if (taskId !== this.dragStartCellInfo.taskId) {
                return;
            }
            
            const currentSelection = new Set();
            const taskDetails = this.tasks[this.dragStartCellInfo.taskId];
            if (!taskDetails) return;

            // Get ordered lists of persons and equipment
            const orderedPersons = this.orderedPersonsForTask(this.dragStartCellInfo.taskId);
            const orderedEquipment = this.orderedEquipmentForTask(this.dragStartCellInfo.taskId);
            
            // Combine both lists for selection while maintaining order
            const allItems = [
                ...orderedPersons.map(id => ({ type: 'person', id })),
                ...orderedEquipment.map(id => ({ type: 'equipment', id }))
            ];

            if (!allItems.length || !this.dates.length || !this.dragStartCellInfo.itemId || !this.dragStartCellInfo.dateFormatted) return;

            const startItemIndex = allItems.findIndex(item => 
                item.type === this.dragStartCellInfo.type && 
                item.id === this.dragStartCellInfo.itemId
            );
            const currentItemIndex = allItems.findIndex(item => 
                item.type === type && 
                item.id === itemId
            );

            const startDateIndex = this.orderedDatesFormatted.indexOf(this.dragStartCellInfo.dateFormatted);
            const currentDateIndex = this.orderedDatesFormatted.indexOf(dateFormatted);

            if (startItemIndex === -1 || currentItemIndex === -1 || startDateIndex === -1 || currentDateIndex === -1) {
                return;
            }

            const minItemIndex = Math.min(startItemIndex, currentItemIndex);
            const maxItemIndex = Math.max(startItemIndex, currentItemIndex);
            const minDateIndex = Math.min(startDateIndex, currentDateIndex);
            const maxDateIndex = Math.max(startDateIndex, currentDateIndex);

            for (let iIdx = minItemIndex; iIdx <= maxItemIndex; iIdx++) {
                const currentItem = allItems[iIdx];
                for (let dIdx = minDateIndex; dIdx <= maxDateIndex; dIdx++) {
                    const dFormatted = this.orderedDatesFormatted[dIdx];
                    currentSelection.add(
                        this.getCellIdentifier(
                            currentItem.type,
                            currentItem.id,
                            dFormatted,
                            this.dragStartCellInfo.taskId
                        )
                    );
                }
            }
            this.selectedCells = currentSelection;
        },

        _handleDocumentMouseUp(event) {
            if (this.isDragging) {
                this.isDragging = false;

                if (this.selectedCells.size <= 1) {
                    this.selectedCells.clear();
                }
                
                document.removeEventListener('mousemove', this.boundHandleDocumentMouseMove);
                document.removeEventListener('mouseup', this.boundHandleDocumentMouseUp);
                this.boundHandleDocumentMouseMove = null;
                this.boundHandleDocumentMouseUp = null;
            }
        },

        handlePlanSelection() {
            const selectionDetails = [];
            for (const cellId of this.selectedCells) {
                const parts = cellId.split('_');
                const type = parts[0];
                const taskId = parts[1]; 
                const itemId = parts[2]; 
                const dateFormatted = parts[3];
                
                let detail = { taskId, dateFormatted, type, isDaySummary: false };
                if (type === 'person') {
                    detail.personId = itemId;
                } else if (type === 'equipment') {
                    detail.equipmentId = itemId;
                }
                selectionDetails.push(detail);
            }

            emitter.emit('open-individual-planning-form', selectionDetails);
            this.selectedCells.clear();
        },

        handleClearSelection() {
            this.selectedCells.clear();
        },

        dayStyleEquipment(date, equipmentId, taskIdForSelectionCheck) {
            let dayColor = '#FFF';
            let finalBorderStyles = {
                borderColor: '#D3D3D3',
                borderWidth: '1px',
                borderStyle: 'solid'
            };

            if (this.isCellSelected('equipment', equipmentId, date.formatted, taskIdForSelectionCheck)) {
                finalBorderStyles.borderColor = '#FFD700';
                finalBorderStyles.borderWidth = '2px';
            }

            return {
                width: ZOOM_SETTINGS[this.currentZoom].dayWidth[date.localDate.dayOfWeek().value()] + 'px',
                background: dayColor,
                ...finalBorderStyles,
            };
        },

        getBlocksForDateEquipmentTask(date, equipmentId, taskId) {
            const equipment = this.equipments[equipmentId];
            if (!equipment?.slots) {
                return [];
            }

            const slotsByDate = equipment.slots[date.formatted] ?? {};
            const slotsForCurrentTask = slotsByDate[taskId] ?? [];
            const otherTaskSlots = Object.entries(slotsByDate)
                .filter(([taskKey]) => Number(taskKey) !== taskId)
                .flatMap(([, slotIds]) => slotIds);

            const resultBlocks = [];

            slotsForCurrentTask.forEach(slotId => {
                resultBlocks.push(this.createBlockObjectEquipment(equipment, slotId, taskId));
            });

            otherTaskSlots.forEach(slotId => {
                resultBlocks.push(this.createBlockObjectEquipment(equipment, slotId, taskId, 0.3));
            });

            return resultBlocks;
        },

        createBlockObjectEquipment(equipment, slotId, actualTaskId, opacity = 1) {
            const slot = this.slots[slotId];
            if (!slot) return null;
            const task = this.tasks[slot.task_id];
            if (!task) return null;
            const project = Array.isArray(this.projects) ? this.projects.find(p => p.id === task.project_id) : this.projects[task.project_id];
            
            return {
                slot_id: slot.id,
                task_title: task.title,
                project_title: project ? project.title : '',
                style: {
                    backgroundColor: task.status_color || '#D3D3D3',
                    color: task.status_text_color || '#000',
                    opacity: opacity,
                    height: this.calculateBlockHeightEquipment(equipment, slot.date, slot.duration) + '%',
                }
            };
        },

        calculateBlockHeightEquipment(equipment, date, duration) {
            return duration > 0 ? 100 : 0;
        },

        openPlanningFormForTaskAndDate(date, taskId) {
            emitter.emit('open-individual-planning-form', [{
                taskId,
                dateFormatted: date.formatted,
                isDaySummary: true
            }]);
        },

        handleClearPlanning() {
            this.showClearPlanningConfirmation = true;
        },

        confirmClearPlanning() {
            this.showClearPlanningConfirmation = false;
            
            // Prepare data for each selected cell
            const cellsData = [];
            for (const cellId of this.selectedCells) {
                const parts = cellId.split('_');
                const type = parts[0];
                const taskId = parseInt(parts[1]);
                const itemId = parseInt(parts[2]);
                const dateFormatted = parts[3];
                
                cellsData.push({
                    taskId,
                    assetId: itemId,
                    assetType: type,
                    date: dateFormatted
                });
            }

            if (cellsData.length === 0) {
                return;
            }

            // Send data to backend
            axios.delete(route('planning.project-persons.clear-selection-slots'), {
                data: {
                    cells: cellsData
                }
            })
            .then(response => {
                emitter.emit('planning-slots-changed', response.data);
                this.selectedCells.clear();
            })
            .catch(error => {
                console.error('Error clearing planning:', error);
            });
        },
    },
    watch: {
        dates: function () {
            this.getData();
        },
        filter: {
            handler: function () {
                this.getData();
            },
            deep: true
        },
        filterLoaded: function () {
            this.getData();
        },
    },
    created(){
        this.getData();
        emitter.on('planning-slots-changed', () => {
            this.getData();
        });
    },
    beforeUnmount(){
        emitter.off('planning-slots-changed', this.getData);
        if (this.isDragging) {
             document.removeEventListener('mousemove', this.boundHandleDocumentMouseMove);
             document.removeEventListener('mouseup', this.boundHandleDocumentMouseUp);
             this.boundHandleDocumentMouseMove = null;
             this.boundHandleDocumentMouseUp = null;
        }
    }
}
</script>

<style scoped lang="scss">
.project-persons-calendar{
    display: inline-block;
    background: #FFF;

    .task-row{
        display: flex;
        background: #EEE;
    }
    .task_title{
        position: sticky;
        left: 0;
        width: 250px;
        height: 30px;
        background: #FFF;
        border-right: 1px solid #ccc;
        border-bottom: 1px solid #ccc;
        box-sizing: border-box;
        padding-left: 15px;
        z-index: 2;
        font-size: 14px;
        line-height: 15px;
    }
    .task_summary-day{
        height: 30px;
        border: 1px solid #ccc;
        box-sizing: border-box;
        padding-left: 5px;
        padding-top: 5px;
        text-align: left;
    }

    .person-row{
        display: flex;
    }
    .person-name{
        position: sticky;
        left: 0;
        width: 250px;
        height: 40px;
        background: #FFF;
        border-left: 5px solid #ccc;
        border-right: 1px solid #ccc;
        border-bottom: 1px solid #ccc;
        box-sizing: border-box;
        padding-left: 10px;
        line-height: 15px;
        z-index: 1;
        a {
            color: #6e7c7c;
            text-decoration: none;
        }
    }
    .person-tasks{
        height: 40px;
        box-sizing: border-box;
        user-select: none; 
        position: relative; 
        display: flex;
        flex-direction: column;
    }

    .slot-btn{
        width: 100%;
        display: block;
        text-align: left;
        overflow: hidden;
        border: 1px solid #CCC;
        font-size: 12px;
        line-height: 15px;
    }
    .absence-block{
        font-size: 12px;
        width: 100%;
        text-align: left;
        overflow: hidden;
        padding-left: 2px;
        background-image: repeating-linear-gradient(
                -45deg,
                rgba(0, 0, 0, 0.1),
                rgba(0, 0, 0, 0.1) 2px,
                transparent 2px,
                transparent 6px
        );
    }

    .equipment-row {
        display: flex;
    }
    .equipment-name {
        position: sticky;
        left: 0;
        width: 250px;
        height: 40px;
        background: #FFF;
        border-left: 5px solid #9E9E9E;
        border-right: 1px solid #ccc;
        border-bottom: 1px solid #ccc;
        box-sizing: border-box;
        padding-left: 10px;
        line-height: 15px;
        z-index: 1;
        a {
            color: #6e7c7c;
            text-decoration: none;
        }
    }
}

.selection-actions-bar-inline {
    position: fixed;
    bottom: 10px;
    right: 60px;
    padding: 15px;
    background-color: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    z-index: 1050;
    display: flex;
    gap: 8px;
}

.selection-actions-bar-inline .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    line-height: 1.5;
    border-radius: 0.2rem;
}
</style>